import { reactive, toRefs, computed } from 'vue'
import { type Branding } from '/@/data/designer/types'
import { useDesignerApi } from '../api'
import { useAppState } from '/@/App/state'
import { normalizeHex, isDarkColor } from '/@/utils/colors'

interface State {
  id: string | null
  nickname: string | null
  website: string | null
  branding: Branding
}

function create() {
  const api = useDesignerApi()
  const app = useAppState()

  const state = reactive<State>({
    id: null,
    nickname: null,
    website: null,
    branding: {
      backgroundColor: null,
      primaryColor: null,
      secondaryColor: null,
      logoImageUrl: null,
    },
  })

  function clear() {
    state.id = null
    state.nickname = null
    state.branding = {
      backgroundColor: null,
      primaryColor: null,
      secondaryColor: null,
      logoImageUrl: null,
    }
    state.website = null
  }

  async function fetch({ nickname = '' }) {
    if (!nickname) return

    const designer = await api.fetch({ nickname })

    if (!designer) return

    state.id = designer.id
    state.nickname = designer.nickname
    state.website = designer.website || null
    state.branding = designer.branding
  }

  function setBranding(newBranding: Branding) {
    state.branding.primaryColor = newBranding.primaryColor
    state.branding.secondaryColor = newBranding.secondaryColor
    state.branding.backgroundColor = newBranding.backgroundColor
    state.branding.logoImageUrl = newBranding.logoImageUrl
  }

  const hasBranding = computed(() =>
    Boolean(
      state.branding.primaryColor ||
        state.branding.secondaryColor ||
        state.branding.backgroundColor ||
        state.branding.logoImageUrl,
    ),
  )

  const classes = computed(() => {
    if (!app.brandingEnabled) return
    const classes = []
    const primary = normalizeHex(state.branding.primaryColor || undefined)
    const secondary = normalizeHex(state.branding.secondaryColor || undefined)
    if (primary) {
      classes.push('brand-navigation-enabled')
    }
    if (secondary) {
      classes.push('brand-accent-enabled')
      if (isDarkColor(secondary)) classes.push('brand-accent-is-dark')
      else classes.push('brand-accent-is-light')
    }
    return classes.join(' ')
  })
  const navigationIsDark = computed(() => {
    const navigation = normalizeHex(state.branding.primaryColor || undefined)
    return Boolean(navigation && isDarkColor(navigation))
  })

  const cssValues = computed(() => {
    const navigation = normalizeHex(state.branding.primaryColor || undefined)
    const accent = normalizeHex(state.branding.secondaryColor || undefined)

    let navigationText = 'white'
    let navigationLogo = 'white'
    if (navigation && navigationIsDark.value) {
      navigationLogo = /* cream-40 */ '#F7EFE3'
    } else if (navigation) {
      navigationText = /* grey-80 */ '#272727'
      navigationLogo = /* cream-90 */ '#5B3921'
    }

    return {
      navigation: navigation || null,
      accent: accent || null,
      navigationText,
      navigationLogo,
    }
  })

  return reactive({
    ...toRefs(state),
    fetch,
    clear,
    hasBranding,
    setBranding,
    classes,
    cssValues,
    navigationIsDark,
    updateBranding: api.updateBranding,
  })
}

let cachedApi: ReturnType<typeof create>

export function useDesignerBranding() {
  if (!cachedApi) {
    cachedApi = create()
  }
  return cachedApi
}
